import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link } from "react-router-dom";
import BackgroundWrapper from "../BackgroundWrapper";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PlaceIcon from "@mui/icons-material/Place";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import {
  HomeContentSection,
  HomeGallery,
  HomeHighlights,
  HomeReglementCta,
  HomeWhyParticipate,
} from "./HomeMarketingSections";
import { HomePromo2026 } from "./HomePromo2026";
import { homeHeroColumnSx } from "./homeConstants";
import { useAppSelector } from "../../store";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import { resolveTournoiHome } from "../../utils/resolveTournoiHome";

const HomeClosed: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tournoi = useAppSelector(getTournoi);
  const home = resolveTournoiHome(tournoi);
  const cardLiftSx = {
    borderColor: theme.palette.divider,
    boxShadow: isDark
      ? `0 8px 28px ${alpha("#000", 0.35)}`
      : "0 2px 8px rgba(0,0,0,0.04)",
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: isDark
        ? `0 10px 32px ${alpha("#000", 0.42)}`
        : "0 4px 16px rgba(0,0,0,0.08)",
    },
  };

  return (
    <BackgroundWrapper>
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 1100,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
        <Zoom in>
          <Card
            variant="outlined"
            sx={{
              ...cardLiftSx,
              ...homeHeroColumnSx,
              mb: 1.5,
              borderLeft: `6px solid ${theme.palette.success.main}`,
              backgroundColor: isDark
                ? alpha(theme.palette.success.main, 0.14)
                : alpha(theme.palette.success.main, 0.08),
            }}
          >
            <CardContent sx={{ py: 3, "&:last-child": { pb: 3 } }}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Box>
                  <SportsTennisIcon
                    sx={{
                      fontSize: 40,
                      color: "success.main",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    color="text.primary"
                    gutterBottom
                  >
                    Le tournoi est en cours !
                  </Typography>
                  <Typography variant="body1" color="text.primary">
                    Suivez les matchs en direct, retrouvez où joue un concurrent
                    et consultez les résultats.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="center"
                  sx={{ width: "100%", pt: 0.5 }}
                >
                  <Button
                    component={Link}
                    to="/live-find"
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<PlaceIcon />}
                    sx={{
                      fontWeight: 700,
                      px: 3.5,
                      width: { xs: "100%", sm: "auto" },
                      textTransform: "none",
                    }}
                  >
                    Où jouez-vous ?
                  </Button>
                  <Button
                    component={Link}
                    to="/resultats"
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<EmojiEventsIcon />}
                    sx={{
                      fontWeight: 700,
                      px: 3.5,
                      width: { xs: "100%", sm: "auto" },
                      textTransform: "none",
                    }}
                  >
                    Résultats
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Zoom>

        <Zoom in>
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{
              ...homeHeroColumnSx,
              mb: 3,
              px: 1,
            }}
          >
            Les inscriptions en ligne sont closes.
          </Typography>
        </Zoom>

        <Zoom in>
          <Box sx={{ ...homeHeroColumnSx, textAlign: "center", mb: 4 }}>
            <HomePromo2026 />
          </Box>
        </Zoom>

        <HomeGallery />
        <HomeHighlights />
        <HomeWhyParticipate />
        <HomeReglementCta />

        <HomeContentSection
          eyebrow="Infos pratiques"
          title="Comment suivre les matchs ?"
          subtitle="Deux accès pour ne rien manquer du tournoi."
        >
          <Box sx={{ ...homeHeroColumnSx, mx: "auto", width: "100%" }}>
            <Card
              variant="outlined"
              sx={{
                ...cardLiftSx,
                borderLeft: `4px solid ${theme.palette.info.main}`,
                backgroundColor: isDark
                  ? alpha(theme.palette.info.main, 0.1)
                  : alpha(theme.palette.info.main, 0.04),
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box component="ul" sx={{ marginTop: 0, pl: 2.5, mb: 0 }}>
                  <li>
                    <Typography component="span" variant="body2" color="text.primary">
                      <strong>Où jouez-vous ? :</strong> retrouvez rapidement la
                      table d’un joueur (recherche sur mobile, annuaire TV sur
                      grand écran).
                    </Typography>
                  </li>
                  <li>
                    <Typography component="span" variant="body2" color="text.primary">
                      <strong>Résultats :</strong> poules, tableaux et parcours
                      des joueurs.
                    </Typography>
                  </li>
                  <li>
                    <Typography component="span" variant="body2" color="text.primary">
                      <strong>Mise à jour automatique :</strong> données
                      rafraîchies en temps réel.
                    </Typography>
                  </li>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ mt: 2 }}
                  color="text.secondary"
                >
                  {home.spectatorTablesLine}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </HomeContentSection>
      </Box>
    </BackgroundWrapper>
  );
};

export default HomeClosed;
